import React, { createContext, useContext, useState, useEffect } from 'react'
import { apiService } from '@/services/api'
import { useAuth } from '@/contexts/AuthContext'

interface BackgroundTask {
  task_id: string
  name: string
  description: string
  status: 'pending' | 'running' | 'completed' | 'failed'
  created_at: string
  started_at?: string
  completed_at?: string
  progress: number
  progress_message: string
  result?: any
  error?: string
}

interface BackgroundTaskContextType {
  activeTasks: Record<string, BackgroundTask>
  // Legacy rebuild-specific
  isIndexRebuilding: boolean
  rebuildProgress: number
  rebuildMessage: string
  showRebuildNotification: boolean
  startRebuildTask: () => Promise<void>
  dismissNotification: () => void
  // New embedding task tracking
  hasActiveEmbeddingTask: boolean
  activeEmbeddingTasks: BackgroundTask[]
  embeddingProgress: number
  embeddingMessage: string
}

const BackgroundTaskContext = createContext<BackgroundTaskContextType | undefined>(undefined)

export const useBackgroundTasks = () => {
  const context = useContext(BackgroundTaskContext)
  if (context === undefined) {
    throw new Error('useBackgroundTasks must be used within a BackgroundTaskProvider')
  }
  return context
}

export const BackgroundTaskProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAdmin, isAuthenticated } = useAuth()
  const [activeTasks, setActiveTasks] = useState<Record<string, BackgroundTask>>({})
  const [showRebuildNotification, setShowRebuildNotification] = useState(false)

  // Check for active tasks periodically - only for admin users
  useEffect(() => {
    // Skip polling if not admin - the endpoint requires admin access
    if (!isAuthenticated || !isAdmin) {
      return
    }

    const checkActiveTasks = async () => {
      try {
        const response = await apiService.getActiveTasks()
        const tasks = response.active_tasks || {}
        setActiveTasks(tasks)

        // Check if there's an index rebuild task
        const taskValues = Object.values(tasks) as BackgroundTask[]
        const rebuildTask = taskValues.find(
          (task) => task.name === 'embedding_index_rebuild'
        )

        if (rebuildTask) {
          setShowRebuildNotification(true)
        } else {
          setShowRebuildNotification(false)
        }
      } catch (error) {
        console.error('Failed to check active tasks:', error)
      }
    }

    // Check immediately
    checkActiveTasks()

    // Then check every 10 seconds
    const interval = setInterval(checkActiveTasks, 10000)

    return () => clearInterval(interval)
  }, [isAdmin, isAuthenticated])

  const startRebuildTask = async () => {
    try {
      const response = await apiService.rebuildEmbeddingIndex()

      // Force an immediate check for active tasks
      setTimeout(async () => {
        try {
          const activeResponse = await apiService.getActiveTasks()
          setActiveTasks(activeResponse.active_tasks || {})
          setShowRebuildNotification(true)
        } catch (error) {
          console.error('Failed to fetch tasks after starting rebuild:', error)
        }
      }, 500)
    } catch (error) {
      console.error('Failed to start rebuild task:', error)
      throw error
    }
  }

  const dismissNotification = () => {
    setShowRebuildNotification(false)
  }

  // Get rebuild task info (legacy)
  const rebuildTask = (Object.values(activeTasks) as BackgroundTask[]).find(
    (task) => task.name === 'embedding_index_rebuild'
  )

  const isIndexRebuilding = !!rebuildTask && rebuildTask.status !== 'completed' && rebuildTask.status !== 'failed'
  const rebuildProgress = rebuildTask?.progress || 0
  const rebuildMessage = rebuildTask?.progress_message || ''

  // Get all embedding tasks (upload, rebuild, initialize, warmup)
  const embeddingTaskNames = ['embedding_upload', 'embedding_index_rebuild', 'embedding_initialize', 'embedding_warmup']
  const activeEmbeddingTasks = Object.values(activeTasks).filter(
    (task: BackgroundTask) =>
      embeddingTaskNames.includes(task.name) &&
      task.status !== 'completed' &&
      task.status !== 'failed'
  )

  const hasActiveEmbeddingTask = activeEmbeddingTasks.length > 0

  // Use the first active embedding task for progress/message
  const primaryEmbeddingTask = activeEmbeddingTasks[0]
  const embeddingProgress = primaryEmbeddingTask?.progress || 0
  const embeddingMessage = primaryEmbeddingTask?.progress_message || ''

  const contextValue: BackgroundTaskContextType = {
    activeTasks,
    // Legacy
    isIndexRebuilding,
    rebuildProgress,
    rebuildMessage,
    showRebuildNotification,
    startRebuildTask,
    dismissNotification,
    // New
    hasActiveEmbeddingTask,
    activeEmbeddingTasks,
    embeddingProgress,
    embeddingMessage
  }

  return (
    <BackgroundTaskContext.Provider value={contextValue}>
      {children}
    </BackgroundTaskContext.Provider>
  )
}